import { useCallback, useEffect, useRef } from 'react';
import { useAddContentModal } from '@/features/events/components/AddContent/store';
import { useShareModalStore } from '@/features/permissions/components/ShareModal/store';
import { InteractionAppNames } from '@/types/gql.generated';
import { useCustomizeMode } from '../CustomizeMode';
import type { Surface } from './steps';

/**
 * Puts the planner into the state the current card is describing, so the panel
 * being explained is actually open behind it.
 *
 * Applied only when the card changes, never on every render: the author is free
 * to close a panel, or open a different one, without the guide dragging them
 * back to its own idea of where they should be.
 */
export const useStepSurface = (
  surface: Surface | null,
  isActive: boolean,
  isResumed = false
) => {
  const { setActiveControl, exitCustomizeMode, openSourcesWithAdd, setAction } =
    useCustomizeMode();
  const {
    openGuided: openShare,
    close: closeShare,
    setGuideHold: setShareGuideHold,
  } = useShareModalStore();
  const {
    openGuided: openAddContent,
    close: closeAddContent,
    setGuideHold: setAddContentGuideHold,
  } = useAddContentModal();
  const applied = useRef<Surface | null>(null);
  const hasApplied = useRef(false);

  // Effect deps deliberately exclude the customize actions: they are rebuilt
  // every render, and depending on them would re-assert the surface constantly.
  const actions = useRef({
    setActiveControl,
    exitCustomizeMode,
    openSourcesWithAdd,
    setAction,
    openShare,
    closeShare,
    setShareGuideHold,
    openAddContent,
    closeAddContent,
    setAddContentGuideHold,
  });
  // Refreshed after the commit rather than during render: a render React throws
  // away would otherwise publish callbacks that never committed, and clearSurface
  // reaches into this ref from an event handler long afterwards. Declared ahead
  // of the effect below so the surface it applies only ever sees committed ones.
  useEffect(() => {
    actions.current = {
      setActiveControl,
      exitCustomizeMode,
      openSourcesWithAdd,
      setAction,
      openShare,
      closeShare,
      setShareGuideHold,
      openAddContent,
      closeAddContent,
      setAddContentGuideHold,
    };
  });

  const releaseModal = useCallback((opened: Surface | null) => {
    if (opened === 'share') actions.current.closeShare();
    if (opened === 'magic') actions.current.closeAddContent();
  }, []);

  useEffect(() => {
    // Deliberately tears nothing down here, and deliberately keeps remembering
    // what it opened. `isActive` is derived from the store, the current user and
    // the flag, and blips false while those settle, which was enough to close
    // the panel the card had just opened. Ending the guide is an explicit event,
    // so `clearSurface` handles the teardown, and forgetting here would leave
    // it with nothing to close when it is finally called.
    if (!isActive) return;

    const {
      setActiveControl: setControl,
      exitCustomizeMode: exitCustomize,
      openSourcesWithAdd: openSources,
      setAction: setInteraction,
      openShare: openShareModal,
      openAddContent: openAddContentModal,
    } = actions.current;

    // The customize panels all write the same `edit` param, so opening one
    // replaces the last. The Share and Add modals are store-driven and the Send
    // sidebar owns a param of its own, so none of them closes on its own and
    // each would otherwise outlive the card that opened it.
    const closeCarriedOver = (previous: Surface | null) => {
      if (previous !== surface) releaseModal(previous);
      if (previous === 'send' && surface !== 'send') setInteraction(null);
    };

    // A beat with no surface of its own leaves the planner as it found it, with
    // one exception: a surface that covers the page cannot stay up over the card
    // that replaced it. Back out of Collaborate lands on exactly such a beat,
    // and was leaving the Share modal over the panel the new card points at.
    if (surface === null) {
      closeCarriedOver(applied.current);
      applied.current = null;
      return;
    }

    if (applied.current === surface) return;
    const previous = applied.current;
    applied.current = surface;

    closeCarriedOver(previous);

    const isDeferred = isResumed && !hasApplied.current;
    hasApplied.current = true;

    switch (surface) {
      case 'magic':
        if (!isDeferred) openAddContentModal();
        return;
      case 'sources':
        // Straight to Add source: the card is telling them to connect a
        // calendar, and a planner with no feeds otherwise lands on the
        // "No sources" placeholder.
        openSources();
        return;
      case 'views':
        setControl('views');
        return;
      case 'style':
        setControl('style');
        return;
      case 'share':
        if (!isDeferred) openShareModal();
        return;
      case 'send':
        setInteraction(InteractionAppNames.AddToCalendar);
        return;
      case 'none':
        // Clears whichever panel the previous card opened, including the Send
        // sidebar: exitCustomize strips edit and action together.
        exitCustomize();
        return;
    }
  }, [surface, isActive, isResumed, releaseModal]);

  useEffect(() => {
    if (!isActive) return;

    const { setShareGuideHold, setAddContentGuideHold } = actions.current;
    setShareGuideHold(applied.current === 'share');
    setAddContentGuideHold(applied.current === 'magic');

    return () => {
      setShareGuideHold(false);
      setAddContentGuideHold(false);
    };
  }, [surface, isActive]);

  /**
   * Closes whatever the guide opened. Called when the guide ends, so finishing
   * on the last card hands the calendar back rather than leaving the Send
   * sidebar over it, and skipping mid-Collaborate cannot strand the Share modal
   * open with its focus trap still yielded to a card that is gone.
   */
  const clearSurface = useCallback(() => {
    const opened = applied.current;
    applied.current = null;

    releaseModal(opened);
    actions.current.setShareGuideHold(false);
    actions.current.setAddContentGuideHold(false);
    // Chat to edit can have moved the modal's chat into the sidebar.
    if (opened && opened !== 'none' && opened !== 'share') {
      actions.current.exitCustomizeMode();
    }
  }, [releaseModal]);

  return { clearSurface };
};
